import { HttpCacheManager } from '@ngneat/cashew';

/** Translations change rarely, so cached responses stay valid for a full day. */
export const TranslocoCacheTime = 1000 * 60 * 60 * 24; // 1 day

/**
 * Storage the translation responses are cached in.
 *
 * `provideHttpCache(withLocalStorage())` only *registers* cashew's
 * localStorage services — since cashew 5 every request picks its storage
 * itself through the `storage` option of `withCache()`, and the default is
 * still the in-memory store. Without this option the translation cache was
 * gone after every reload, so each page load refetched all ~20 transloco
 * scopes (`classic.component.ts`) over the network (HIP-50).
 */
export const TRANSLATION_CACHE_STORAGE = 'localStorage' as const;

/** localStorage key holding the backend's last translation update timestamp. */
export const TRANSLATION_TIMESTAMP_KEY = 'translation_timestamp';

/** localStorage key holding the timestamp the cached translations belong to. */
const TRANSLATION_CACHE_VERSION_KEY = 'translation_cache_version';

/** Shared prefix of every translation cache key — see `buildTranslationCacheKey`. */
const CACHE_KEY_PREFIX = 'translate-';

/**
 * Bumped whenever the cache key layout changes, so the first load of the new
 * build purges the entries the old layout left behind. `v2` dropped the uid
 * from the key (HIP-53) — without this every user would keep ~300 KB of
 * per-login junk in localStorage until the 24h TTL of each entry expired.
 */
const CACHE_SCHEMA_VERSION = 'v2';

/** Prefixes cashew puts in front of a cache key when it writes to a browser storage. */
const STORAGE_PREFIXES = ['@cache-', '@ttl-'];

/**
 * Cache key of one translation scope.
 *
 * Deliberately *not* keyed by uid: the backend answers `System_translations.php`
 * purely by `iso_code` + `module`, while the uid is a fresh PHP session token on
 * every single login. Keying by it threw the whole 24h cache away at each login
 * — the loader refetched all ~60 scope/language combinations, which the PHP
 * session lock serialises into seconds of waiting (HIP-53). The anonymous
 * responses the uid used to fence off are kept out by the caller instead: it
 * only caches once a uid exists, and never stores an empty payload.
 */
export function buildTranslationCacheKey(
    module: string,
    language: string
): string {
    return `${CACHE_KEY_PREFIX}${module}-${language}`;
}

/**
 * The translation revision the cache should hold: the backend's last
 * translation update timestamp, as fetched by the `TranslationResolver`,
 * combined with the key layout the entries were written in.
 */
export function currentTranslationVersion(): string {
    const timestamp = readItem(TRANSLATION_TIMESTAMP_KEY) ?? 'initial';
    return `${CACHE_SCHEMA_VERSION}:${timestamp}`;
}

/** The translation revision the cached responses were fetched for. */
export function cachedTranslationVersion(): string | null {
    return readItem(TRANSLATION_CACHE_VERSION_KEY);
}

export function rememberTranslationVersion(version: string): void {
    writeItem(TRANSLATION_CACHE_VERSION_KEY, version);
}

/**
 * Drops every cached translation response.
 *
 * `HttpCacheManager.clear()` is not enough: cashew's browser-storage adapters
 * only clear the entries they touched in the current session, so translations
 * written before a reload would survive until the 24h TTL. Scanning
 * localStorage catches those too, and routing the deletes through the cache
 * manager keeps its in-memory mirror in sync.
 */
export function clearTranslationCache(cacheManager: HttpCacheManager): void {
    for (const key of storedTranslationCacheKeys()) {
        cacheManager.delete(key, { strategy: TRANSLATION_CACHE_STORAGE });
    }
}

function storedTranslationCacheKeys(): string[] {
    const keys = new Set<string>();
    try {
        for (let index = 0; index < localStorage.length; index++) {
            const storageKey = localStorage.key(index);
            if (!storageKey) {
                continue;
            }
            const prefix = STORAGE_PREFIXES.find(candidate =>
                storageKey.startsWith(`${candidate}${CACHE_KEY_PREFIX}`)
            );
            if (prefix) {
                keys.add(storageKey.slice(prefix.length));
            }
        }
    } catch {}
    return [...keys];
}

// localStorage can be unavailable (private mode, blocked storage) — never throw.
function readItem(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeItem(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {}
}
